<script setup>
import { computed } from 'vue';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  analysisOptions: {
    type: Object,
    default: () => ({
      include_resolved: false,
      min_hours_open: 0,
    }),
  },
});

const emit = defineEmits(['toggle', 'update:analysis-options', 'close']);

const localOptions = computed({
  get: () => props.analysisOptions,
  set: value => emit('update:analysis-options', value),
});

const updateOption = (key, value) => {
  emit('update:analysis-options', {
    ...props.analysisOptions,
    [key]: value,
  });
};
</script>

<template>
  <div
    class="mb-6 p-4 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container"
  >
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN_ANALYTICS.SETTINGS.TITLE') }}
      </h3>
      <button
        class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11"
        @click="emit('close')"
      >
        <span class="i-lucide-x text-lg" />
      </button>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Inbox Toggles -->
      <div>
        <h4 class="text-sm font-medium text-n-slate-11 mb-3">
          {{ $t('CAPTAIN_ANALYTICS.SETTINGS.INBOXES') }}
        </h4>
        <div class="space-y-2 max-h-48 overflow-y-auto">
          <div
            v-for="inbox in inboxes"
            :key="`${inbox.id}-${inbox.captain_analytics_enabled}`"
            class="flex items-center justify-between p-2 rounded-lg hover:bg-n-alpha-1 cursor-pointer"
            @click="emit('toggle', inbox.id, !inbox.captain_analytics_enabled)"
          >
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
            <div
              class="relative w-10 h-6 rounded-full transition-colors"
              :class="
                inbox.captain_analytics_enabled
                  ? 'bg-n-green-9'
                  : 'bg-n-slate-6'
              "
            >
              <span
                class="absolute top-1 w-4 h-4 rounded-full bg-white transition-transform"
                :class="inbox.captain_analytics_enabled ? 'left-5' : 'left-1'"
              />
            </div>
          </div>
          <div
            v-if="inboxes.length === 0"
            class="text-sm text-n-slate-10 text-center py-4"
          >
            {{ $t('CAPTAIN_ANALYTICS.SETTINGS.NO_INBOXES') }}
          </div>
        </div>
      </div>

      <!-- Analysis Options -->
      <div>
        <h4 class="text-sm font-medium text-n-slate-11 mb-3">
          {{ $t('CAPTAIN_ANALYTICS.SETTINGS.ANALYSIS_OPTIONS') }}
        </h4>
        <div class="space-y-4">
          <div class="flex items-center gap-3">
            <input
              id="include_resolved"
              type="checkbox"
              class="w-4 h-4 rounded border-n-container text-n-blue-9 focus:ring-n-blue-9"
              :checked="localOptions.include_resolved"
              @change="updateOption('include_resolved', $event.target.checked)"
            />
            <label for="include_resolved" class="text-sm text-n-slate-12">
              {{ $t('CAPTAIN_ANALYTICS.SETTINGS.INCLUDE_RESOLVED') }}
            </label>
          </div>

          <div class="flex flex-col gap-1">
            <label for="min_hours" class="text-sm text-n-slate-12">
              {{ $t('CAPTAIN_ANALYTICS.SETTINGS.MIN_HOURS') }}
            </label>
            <input
              id="min_hours"
              type="number"
              min="0"
              class="px-3 py-2 rounded-lg border border-n-container bg-n-solid-1 text-n-slate-12 w-32"
              :value="localOptions.min_hours_open"
              @input="
                updateOption(
                  'min_hours_open',
                  parseInt($event.target.value) || 0
                )
              "
            />
            <span class="text-xs text-n-slate-10">
              {{ $t('CAPTAIN_ANALYTICS.SETTINGS.MIN_HOURS_HELP') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
